<template>
    <div class="annotation-card">
        <template-card-v2
            :hasHistory="false"
            :label="label"
            :linkedid="linkedid"
            :linkedtype="linkedtype"
            :oneLog="oneLog"
            class="annotation-card"
        >
            <!-- LABEL -->
            <template #icon>
                <CompassObject object="comment" size="small" />
            </template>
            <template #label>
                <person-list-light
                    :list="[oneLog.owner]"
                    placeholderSingular="Collaborateur"
                    placeholderPlural="Collaborateurs"
                ></person-list-light>
            </template>

            <!-- COMMENT -->
            <template #content>
                <div>
                    <div>
                        <!-- NAME AND DATE -->
                        <div class="wp-10 info flex-level flex-top">
                            <div class="wp-10">
                                <!-- TEXT -->
                                <div v-if="!editMode">
                                    <div
                                        v-if="oneLog.timetracking && oneLog.timetracking.service"
                                        class="typographies-paragraph-small-regular annotation-card-timer__container"
                                    >
                                        <span>
                                            {{ oneLog.timetracking.service }}
                                        </span>
                                        <span v-if="oneLog.timetracking.timeFormatted.length"
                                            >-
                                        </span>
                                        <span
                                            v-if="oneLog.timetracking.timeFormatted.length"
                                            class="annotation-card-timer__content-time"
                                        >
                                            <CompassIcon
                                                type="secondary"
                                                size="extra-small"
                                                icon="nav-timetracking"
                                                class="mx-1 gray-80"
                                            />
                                            {{ oneLog.timetracking.timeFormatted }}
                                        </span>
                                    </div>
                                    <div class="content">
                                        <div
                                            :id="`annotContent${oneLog.objectid}`"
                                            :ref="`annotContent${oneLog.objectid}`"
                                            class="editor-preview typographies-paragraph-medium-regular rich-text-style annotation-card__content"
                                            :class="editorClass"
                                        />
                                        <CompassButton
                                            v-if="oneLog.content.length > 256"
                                            class="annotation-card__button-read"
                                            :label="
                                                fullText
                                                    ? labelsButton.readLess
                                                    : labelsButton.readMore
                                            "
                                            type="secondary"
                                            size="small"
                                            @click.stop="fullText = !fullText"
                                        />
                                    </div>
                                </div>

                                <!-- EDIT -->
                                <el-form
                                    v-else
                                    ref="updateAnnotationForm"
                                    class="mb-0 annotation-card-edit__container"
                                    :rules="rules"
                                    :model="ruleForm"
                                >
                                    <RichText
                                        v-model="tmpContent"
                                        :options="optionsRichText"
                                        typeRichText="comment"
                                        :refRichText="refRichTextEdit"
                                        :showAIComponents="showAIComponents"
                                        :hash="hashRichTextEdit"
                                        class="annotation-richtext"
                                    >
                                        <template v-if="timetrackingEnabled" #more-custom-options>
                                            <TimetrackingRichText
                                                v-model="ruleForm"
                                                :showMenuTimetracking="displayTimetrackingForm"
                                                mode="edit"
                                                :defaultOptions="defaultOptions"
                                                :refRichText="refRichTextEdit"
                                            />
                                        </template>
                                        <template v-if="canSaveAnnotation" #actions>
                                            <CompassButton
                                                :label="$t.__('common.actions.cancel')"
                                                type="secondary"
                                                size="small"
                                                @click.prevent="cancel()"
                                            />
                                            <CompassButton
                                                :label="$t.__('common.actions.save')"
                                                type="primary"
                                                size="small"
                                                :loading="confirmLoading"
                                                @click.prevent="
                                                    submitForm(
                                                        'updateAnnotationForm',
                                                        'submitUpdate',
                                                        {
                                                            id: oneLog.objectid,
                                                        },
                                                    )
                                                "
                                            />
                                        </template>
                                    </RichText>
                                </el-form>
                                <div v-if="mentionAddedInEditing" class="fs-2 gray-80 mt-1">
                                    {{
                                        $l10n.__(
                                            "Les notifications ne sont pas envoyées lors d'une édition de commentaire",
                                        )
                                    }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="mt-3">
                    <div v-if="replies && replies.length > 0" class="annotation-card__replies">
                        <!-- TOGGLE BTN -->
                        <div
                            v-if="replies.length > 2"
                            class="annotation-card__replies--more prelative zi-1"
                        >
                            <CompassButton
                                :label="seeMoreAnswers"
                                type="secondary"
                                @click="toggleAllAnnot()"
                            ></CompassButton>
                        </div>

                        <!-- FIRST 2 ANNOTATIONS -->
                        <ul v-if="!displayAllAnnot" class="m-0">
                            <li
                                v-for="reply in last2annot"
                                :key="reply.id"
                                class="relative flex-level flex-top annotation-card__item"
                            >
                                <AnnotationCardReply :reply="reply" @update-reply="updateReply" />
                            </li>
                        </ul>

                        <!-- ALL ANNOTATIONS -->
                        <ul v-else class="m-0">
                            <li
                                v-for="reply in replies"
                                :key="reply.id"
                                class="relative flex-level flex-top annotation-card__item"
                            >
                                <AnnotationCardReply :reply="reply" @update-reply="updateReply" />
                            </li>
                        </ul>
                    </div>

                    <div
                        class="flex-row annotation-card-reply-action__container br annotation-reply-container"
                    >
                        <CompassAvatar
                            :initials="getUserInitials"
                            :avatarColorType="avatarColor(getUserName)"
                            size="extra-small"
                        ></CompassAvatar>
                        <RichText
                            v-model="newReply"
                            :placeholder="$t.__('common.actions.answer_message')"
                            :options="optionsRichText"
                            typeRichText="comment"
                            class="annotation-richtext"
                            :useStorage="true"
                            :refRichText="refRichText"
                            :hash="hashRichTextReply"
                            :showAIComponents="showAIComponents"
                        >
                            <template v-if="canSaveReply" #actions>
                                <CompassButton
                                    :label="$t.__('common.actions.cancel')"
                                    type="secondary"
                                    size="small"
                                    @click.prevent="resetAnnotReply"
                                />
                                <CompassButton
                                    :label="$t.__('common.actions.save')"
                                    type="primary"
                                    size="small"
                                    :loading="confirmLoading"
                                    @click.prevent="submitReply"
                                />
                            </template>
                        </RichText>
                    </div>
                </div>
            </template>
        </template-card-v2>
    </div>
</template>

<script>
import TemplateCardV2 from './TemplateCardV2.vue';
import { getFullNameUser } from 'Helpers/avatarHelper';
import AnnotationCardReply from 'vcomponents/timeline/AnnotationCardReply.vue';
import FormMixin from '../../mixins/FormMixins';
import AnnotationValidator from '../../mixins/validators/AnnotationValidator';
import TimelineCardMixin from './TimelineCardMixin.js';
import PersonListLight from 'vcomponents/utilities/PersonListLight.vue';
import AnnotationService from 'Services/Annotations/AnnotationService';
import AvatarMixins from 'mixins/AvatarMixins';
import { createNamespacedHelpers } from 'vuex';
import { truncateHTML } from 'js/vue/helpers/truncateHTML.js';
import RichText from 'vcomponents/richtext/RichText.vue';
import { REF_RICH_TEXT_CONTEXT, buildRichTextRef } from 'Services/RichTextRefBuilder.js';
const { mapState: mapStateUser } = createNamespacedHelpers('user');
import TimetrackingRichText from 'vcomponents/richtext/customoptions/TimetrackingRichText.vue';
import generateHash from 'Helpers/hashGenerator.js';
import Feature from 'Services/Feature';
import { editorClass } from 'Helpers/richTextHelper.js';
import { useBuildMention } from 'composables/useBuildMention';

export default {
    components: {
        TemplateCardV2,
        AnnotationCardReply,
        PersonListLight,
        RichText,
        TimetrackingRichText,
    },
    mixins: [FormMixin, AnnotationValidator, AvatarMixins, TimelineCardMixin],
    data() {
        return {
            label: this.$l10n.__('Commentaire'),
            tmpContent: '',
            newReply: '',
            fullText: false,
            displayAllAnnot: false,
            editMode: false,
            deleteModal: false,
            timetrackingEnabled: 'undefined' !== typeof this.oneLog.timetracking,
            timetrackingFormat:
                this.oneLog.timetracking && this.oneLog.timetracking.format
                    ? this.oneLog.timetracking.format
                    : 'HH:mm',
            timetrackingSubmit: false,
            defaultOptions:
                this.oneLog.timetracking && this.oneLog.timetracking.serviceid
                    ? [
                          {
                              id: this.oneLog.timetracking.serviceid,
                              name: this.oneLog.timetracking.service,
                          },
                      ]
                    : [],
            rules: {
                timetrackingTime: [{ validator: this.validateTimetracking, trigger: 'blur' }],
                service: [{ validator: this.validateService, trigger: 'blur' }],
            },
            ruleForm: {
                service:
                    this.oneLog.timetracking && this.oneLog.timetracking.serviceid
                        ? parseInt(this.oneLog.timetracking.serviceid)
                        : 0,
                timetrackingTime:
                    this.oneLog.timetracking && this.oneLog.timetracking.time
                        ? this.oneLog.timetracking.time
                        : '',
            },
            displayTimetrackingForm: false,
            annotationService: new AnnotationService(),
            mentionAddedInEditing: false,
            replies: [],
            optionsRichText: [
                'SellsyAI',
                'Heading',
                'Bold',
                'Italic',
                'Underline',
                'Color',
                'Highlight',
                'BulletList',
                'OrderedList',
                'Divider',
                'Link',
                'Mention',
            ],
            options: [],
            confirmLoading: false,
            refRichText: '',
            refRichTextEdit: '',
            labelsButton: {
                readMore: this.$l10n.__('Lire plus'),
                readLess: this.$l10n.__('Lire moins'),
            },
            hashRichTextEdit: generateHash(),
            hashRichTextReply: generateHash(),
        };
    },

    computed: {
        ...mapStateUser({
            userForename: 'forename',
            userName: 'name',
        }),
        showAIComponents() {
            return Feature.isEnabled('sellsy-ai');
        },
        editorClass,
        canSaveAnnotation() {
            return this.tmpContent.length > 0 && this.tmpContent.replace(/\s/gm, '') !== '<p></p>';
        },
        canSaveReply() {
            return this.newReply.length > 0 && this.newReply.replace(/\s/gm, '') !== '<p></p>';
        },
        getUserInitials() {
            return this.userForename[0] + this.userName[0];
        },
        getUserName() {
            return `${this.userForename} ${this.userName}`;
        },
        last2annot() {
            return this.replies.slice(-2);
        },
        seeMoreAnswers() {
            return !this.displayAllAnnot
                ? this.$l10n.n__(
                      'Afficher un commentaire de plus',
                      'Afficher les %s commentaires',
                      this.replies.length,
                      this.replies.length,
                  )
                : this.$l10n.__('Masquer tous les commentaires');
        },
    },
    watch: {
        fullText() {
            this.$nextTick(() => this.htmlFormattedContent());
        },
        editMode(newEditMode) {
            if (!newEditMode) {
                this.$nextTick(() => this.htmlFormattedContent());
            }
        },
    },
    created() {
        this.refRichText = buildRichTextRef({
            context: REF_RICH_TEXT_CONTEXT.REPLY_NEW,
            linkedid: this.linkedid,
            linkedtype: this.linkedtype,
            cardId: this.oneLog.objectid,
        });
        this.refRichTextEdit = buildRichTextRef({
            context: REF_RICH_TEXT_CONTEXT.ANNOT_EDIT,
            linkedid: this.linkedid,
            linkedtype: this.linkedtype,
            cardId: this.oneLog.objectid,
        });
        this.oneLog.replies.forEach((reply) => {
            this.createReplyWithApiV2Format(reply);
        });
        this.replies = this.oneLog.replies;

        EventBus.on('timeline:editAnnotation', (event) => {
            if (this.oneLog.objectid === event) {
                this.toggleEditMode();
            }
        });
    },
    mounted() {
        this.htmlFormattedContent();
    },
    methods: {
        htmlFormattedFullContent(contentFormatted, oneLog) {
            const content = truncateHTML(oneLog);
            return content;
        },

        htmlFormattedContent() {
            const contentFormatted = document.createElement('div', {});
            const content = this.oneLog.content.replaceAll('<p></p>', '<p><br /></p>');

            contentFormatted.innerHTML = this.fullText
                ? content
                : this.htmlFormattedFullContent(contentFormatted, content);

            contentFormatted.querySelectorAll('.mention').forEach((mention) => {
                const mentionBuilder = useBuildMention().buildMention(
                    mention,
                    this.$store.getters['user/getUser'].id,
                );
                mention.parentNode.replaceChild(mentionBuilder, mention);
            });

            if (!this.$refs[`annotContent${this.oneLog.objectid}`].innerHTML) {
                this.$refs[`annotContent${this.oneLog.objectid}`]
                    .appendChild(contentFormatted)
                    .setAttribute('id', `annotation__content-${this.oneLog.objectid}`);
            } else {
                this.$refs[`annotContent${this.oneLog.objectid}`].replaceChild(
                    contentFormatted,
                    document.getElementById(`annotation__content-${this.oneLog.objectid}`),
                );
                this.$refs[`annotContent${this.oneLog.objectid}`].children[0].setAttribute(
                    'id',
                    `annotation__content-${this.oneLog.objectid}`,
                );
            }
        },
        createReplyWithApiV2Format(data) {
            const owner = data._embed ? data._embed.owner : data.owner;
            //if _embed => data from apiV2 else data form rest route
            let timestamp = 0;
            if (!data._embed) {
                if (data.owner.avatar && data.owner.avatar.type === 'picture') {
                    owner.avatar = data.owner.avatar.value;
                } else {
                    owner.avatar = '';
                }
                timestamp = data.timestamp;
                owner.firstname = owner.name;
            } else {
                owner.avatar = data._embed.owner.avatar;
                owner.name = getFullNameUser({
                    firstname: owner.firstname,
                    lastname: owner.lastname,
                });
                timestamp = new Date(data.updated).getTime() / 1000;
            }
            return {
                can_delete: true,
                can_edit: true,
                content: data.description,
                id: data.id,
                owner: owner,
                timestamp: timestamp,
            };
        },
        toggleEditMode() {
            this.editMode = !this.editMode;
            if (this.editMode) {
                this.tmpContent = this.oneLog.content;
                this.defaultOptions =
                    this.oneLog.timetracking && this.oneLog.timetracking.serviceid
                        ? [
                              {
                                  id: this.oneLog.timetracking.serviceid,
                                  name: this.oneLog.timetracking.service,
                              },
                          ]
                        : [];
                // if comment contains @mention, force display mentionAddedInEditing message
                if (
                    /<span[^>]*\bclass="mention"[^>]*\bdata-id="[^"]*"[^>]*>/g.test(
                        this.oneLog.content,
                    )
                ) {
                    this.mentionAddedInEditing = true;
                }
            }
        },
        updateReply(reply) {
            const replyIndex = this.replies?.findIndex(
                (repliesItem) => repliesItem.id === reply.id,
            );

            if (replyIndex !== -1) {
                this.replies[replyIndex].content = reply.content;
            }
        },
        submitReply() {
            this.confirmLoading = true;
            this.annotationService
                .createAnnotation(
                    this.newReply,
                    this.linkedid,
                    this.linkedtype,
                    parseInt(this.oneLog.objectid),
                )
                .then((response) => {
                    if (sessionStorage.getItem(`overview__${this.refRichText}`)) {
                        sessionStorage.removeItem(`overview__${this.refRichText}`);
                    }
                    const reply = this.createReplyWithApiV2Format(response.data);
                    this.newReply = '';
                    const replies = this.replies;
                    replies.push(reply);
                    this.$emit('changeLog', { ...this.oneLog, replies });
                })
                .catch((err) => {
                    this.errors.setErrors(err.response.data.error.message);
                    this.$pushToast({
                        type: 'error',
                        icon: true,
                        text: this.$l10n.__(
                            "Une erreur est survenue lors de l'enregistrement du commentaire",
                        ),
                    });
                })
                .finally(() => (this.confirmLoading = false));
        },
        // Disable ESlint for next line because this methods is almost used by submitForm Mixin method
        /* eslint-disable-next-line vue/no-unused-properties */
        submitUpdate(payload) {
            this.confirmLoading = true;
            this.mentionAddedInEditing = false;

            let timetracking = null;
            if (this.ruleForm.timetrackingTime.length > 0) {
                const a = this.ruleForm.timetrackingTime.split(':'); // split it at the colons
                timetracking = {
                    duration: +a[0] * 60 * 60 + +a[1] * 60 + +a[2],
                    service: this.ruleForm.service,
                };
            }
            this.annotationService
                .updateAnnotation(payload.id, this.tmpContent, timetracking)
                .then((response) => {
                    const content = response.data.description;

                    this.$emit('changeLog', { ...this.oneLog, content });
                    this.$nextTick(() => {
                        this.editMode = false;
                        this.displayTimetrackingForm = false;

                        if (!_.isNil(response.data.timetracking)) {
                            this.timetrackingFormat =
                                response.data.timetracking.duration >= 3600 ? `HH:mm` : `mm`;

                            const format =
                                response.data.timetracking.duration >= 3600
                                    ? `HH [${this.$l10n.__('heures')}] mm [${this.$l10n.__(
                                          'minutes',
                                      )}]`
                                    : `mm [${this.$l10n.__('minutes')}]`;

                            const stringDurationTimetrackingWithText = moment()
                                .startOf('day')
                                .second(response.data.timetracking.duration)
                                .format(format);
                            const stringDurationTimetracking = moment()
                                .startOf('day')
                                .second(response.data.timetracking.duration)
                                .format('HH:mm:ss');

                            const timetracking = {
                                timeFormatted: stringDurationTimetrackingWithText,
                                time: stringDurationTimetracking,
                                service: response.data.timetracking.name,
                                serviceid: response.data.timetracking.service,
                                format: format,
                            };

                            this.defaultOptions =
                                this.oneLog.timetracking && this.oneLog.timetracking.serviceid
                                    ? [
                                          {
                                              id: this.oneLog.timetracking.serviceid,
                                              name: this.oneLog.timetracking.service,
                                          },
                                      ]
                                    : [];
                            this.$emit('changeLog', { ...this.oneLog, timetracking });
                        }
                    });
                })
                .catch((err) => {
                    this.errors.setErrors(err.response.data.error.message);
                    this.$pushToast({
                        type: 'error',
                        icon: true,
                        text: this.$l10n.__(
                            "Une erreur est survenue lors de l'enregistrement du commentaire",
                        ),
                    });
                })
                .finally(() => (this.confirmLoading = false));
        },
        toggleAllAnnot() {
            this.displayAllAnnot = !this.displayAllAnnot;
        },
        cancel() {
            this.editMode = false;
            this.mentionAddedInEditing = false;
            this.displayTimetrackingForm = false;
        },
        resetAnnotReply() {
            EventBus.emit('annotationTimetracking:reset');
            this.newReply = '';
        },
    },
};
</script>

<style lang="scss">
@use '~@sellsy/styleguide/src/_variables' as *;
.dialog {
    &__default {
        padding-bottom: 24px;
        p {
            margin: 0 0 var(--spacing-3);
        }
        ul {
            margin: 0;
        }
    }

    &__footer {
        display: flex;
        justify-content: flex-end;
        padding: 0 32px 24px 32px;
    }
}
.annotation-card {
    &__content {
        word-break: break-word;
    }
    &__item:not(:last-child),
    &__replies {
        margin-bottom: 12px;
    }

    &__replies--more {
        margin-bottom: 12px;
    }

    &__button-read-more {
        margin-top: var(--spacing-0);
    }

    .content {
        display: grid;
        gap: var(--spacing-0);
    }
    &__button-read {
        width: fit-content;
    }
    .annotation-form {
        border: 1px solid $color-26;
        border-radius: 4px;
    }
    &.highlighted {
        .annotation-card {
            box-shadow: 0 0 8px rgba(17, 17, 17, 0.2);
        }
    }
    &-edit__container .ProseMirror {
        min-height: 70px;
    }

    .editor-preview {
        & > div p:first-child {
            margin-top: 0;
        }

        & > div p:last-child {
            margin-bottom: 0;
        }

        p {
            padding: 0;
            line-height: 20px;
            font-size: 14px;
            margin: 0;
        }
    }
    &-timer {
        &__container {
            display: flex;
            line-height: 16px;
            border: solid var(--colors-common-border-lighter) 1px;
            border-radius: 20px;
            width: max-content;
            padding: 2px var(--spacing-1);
            gap: var(--spacing-0);
            margin-bottom: var(--spacing-0);
            span {
                color: var(--colors-common-text-subtle-default);
            }
        }
        &__content-time {
            display: flex;
            align-items: center;
        }
    }
    .annotation-card-reply-action {
        &__container {
            padding: 12px;
            background: $gray-0;
            .editor-input {
                background: $white;
                .ProseMirror,
                .menu-bar-editor {
                    border: unset;
                }
                .ProseMirror {
                    border-bottom: solid 1px $gray-05;
                    border-radius: 0;
                    padding: 6px 12px;
                }
            }
        }
    }
}
</style>

<style lang="scss" scoped>
.annotation-reply-container {
    display: flex;
    min-height: 0;
    border-radius: 4px;
    padding: var(--spacing-2);
    background: #f6f6f9;
    gap: var(--spacing-0);
}
</style>
